import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, throwError } from 'rxjs';

import { L10N_LOCALE, L10nLocale, L10nTranslationService } from "angular-l10n";
import { map, share } from 'rxjs/operators';
import { oase_config } from '../../environments/environment';
import { APIConfigService } from '../api.config.service';
import { Kernbenutzer } from '../protected/kernbenutzer/kernbenutzer.model';
import { ErrorHelper } from './error.helper';
import { Authority } from './jwt/authority.model';
import { Token } from './jwt/token.model';
import { LoggerService } from './logger.service';
import { AuthResponse } from './response/authresponse.model';
import { SessionService } from './session.service';
import { User } from './user.interface';

const PATH_FILE = 'app/shared/auth.service.ts';

@Injectable()
export class AuthService {
	token: Token = null;
	kernbenutzer: Kernbenutzer = null;

	REST_API_AUTH: string = null;
	REST_API_REFRESH: string = null;
	REST_API_USERDATA: string = null;

	constructor(
		private readonly router: Router,
		private readonly http: HttpClient,
		public jwtHelper: JwtHelperService,
		@Inject(L10N_LOCALE) public locale: L10nLocale,
		public translation: L10nTranslationService,
		public apiConfigService: APIConfigService,
		public sessService: SessionService,
		private readonly _logger: LoggerService) {
			this.REST_API_AUTH = this.apiConfigService.REST_API_BASE() + 'auth';
			this.REST_API_REFRESH = this.apiConfigService.REST_API_BASE() + 'auth/refresh';
			this.REST_API_USERDATA = this.apiConfigService.REST_API_BASE() + 'benutzerdaten';

			this.restoreJwToken();
			if (this.isAuthenticated()) {
				this._logger.debug(PATH_FILE+' - isAuthenticated');
				this.getAndStoreBenutzerdaten();
				this.restoreBenutzerdaten();
		}
	}

	private refreshToken() {
		let new_auth_token: string = null;

		this.http.get(this.REST_API_REFRESH).subscribe({
				next: (data: AuthResponse) => {
					new_auth_token = data.authtoken;
					this._logger.log(PATH_FILE + ' - refreshToken got answer data: '+JSON.stringify(data));
				},
				error: (err) => {
					let fehler = ErrorHelper.handleError(err, 'Fehler beim erneuern des Token', this._logger, this.translation);
					return throwError(() => new Error(fehler.message));
				},
				complete: () => {
					this.storeJwToken(new_auth_token);
					this._logger.log(PATH_FILE + ' - Erneuern des Token abgeschlossen');
				}
			});
	}

	private holeBenutzerdaten(): Observable<Kernbenutzer> {
		return this.http.get<Kernbenutzer>(this.REST_API_USERDATA + '/');
	}

	private restoreJwToken() {
		if (!this.tokenExists()) {
			let token: string = localStorage.getItem(oase_config.auth_token.name);
			if (token != null) {
				this.token = new Token(token, this.jwtHelper);
			}
			let kernbenutzer: string = localStorage.getItem(oase_config.loggedinuser);
			if (token != null) {
				this.kernbenutzer = JSON.parse(kernbenutzer);
			}
		}
	}

	private restoreBenutzerdaten() {
		if (this.kernbenutzer == null) {
			let kernbenutzer: string = localStorage.getItem(oase_config.loggedinuser);
			if (kernbenutzer != null) {
				this.kernbenutzer = JSON.parse(kernbenutzer);
			}
		}
	}

	private tokenExists() {
		return this.token != null;
	}

	private removeTokenFromStore(): void {
		this.token = null;
		localStorage.removeItem(oase_config.auth_token.name);
	}

	private removeBenutzerdatenFromStore(): void {
		this.kernbenutzer = null;
		localStorage.removeItem(oase_config.loggedinuser);
	}

	private getAuthorities(): Array<Authority> {
		if (!this.tokenExists()) {
			return null;
		}
		// Sollte möglicherweise noch geändert werden, also das der Benuztername nicht in sub sonder z.B. in unsername steht!
		return this.token.decoded.at_oase_authorities;
	}

	private getAndStoreBenutzerdaten(eingeloggtseit?: number) {
		this._logger.debug(PATH_FILE+' - getAndStoreBenutzerdaten mit eingeloggtseit: '+eingeloggtseit);
		if(this.kernbenutzer==null) {
      this.holeBenutzerdaten().subscribe((responseKernbenutzer: Kernbenutzer) => {
        if (eingeloggtseit == null && responseKernbenutzer.eingeloggtseit != null && this.kernbenutzer?.eingeloggtseit !== null) {
          responseKernbenutzer.eingeloggtseit = this.kernbenutzer.eingeloggtseit;
        }
        this.kernbenutzer = responseKernbenutzer;
        this.storeBenutzerdaten(this.kernbenutzer, eingeloggtseit);
      });
    }
	}

	private storeBenutzerdaten(kernbenutzer: Kernbenutzer, eingeloggtseit?: number) {
		this.kernbenutzer = kernbenutzer;
		if (eingeloggtseit !== null && this.kernbenutzer!=null && this.kernbenutzer.eingeloggtseit == null) {
			this.kernbenutzer.eingeloggtseit = eingeloggtseit;
		}
		localStorage.setItem(oase_config.loggedinuser, JSON.stringify(this.kernbenutzer));
	}

	/**********************************************************************************************************************/
	/**********************************************************************************************************************/
	/**                                                  PUBLIC METHODS                                                  **/
	/**********************************************************************************************************************/
	/**********************************************************************************************************************/

	public signupUser(user: User) {
		this._logger.warning(PATH_FILE + ' - singupUser aufgerufen, aber die Funktion tut ja nix????');
	}

	public signinUser(signinuser: User): Observable<any> {
		this.sessService.removeCopyInformations();
		let user = signinuser.user;
		let password = signinuser.password;
		this._logger.log(PATH_FILE + ' - signinUser REST_API_AUTH: ', this.REST_API_AUTH);
		this._logger.debug(PATH_FILE + ' - signinUser JSON: ', JSON.stringify({ user, password }));
		return this.http.post(this.REST_API_AUTH, JSON.stringify({ user, password }), {
				headers: new HttpHeaders({ 'Content-Type': 'application/json' })
			});
	}

	public logout() {
		this._logger.log(PATH_FILE + ' - logout');
		this.removeTokenFromStore();
		this.sessService.removeSessionStore();
		this.router.navigate(['/signin']);
	}

	public updateBenutzerdaten(kernbenutzer: Kernbenutzer): Observable<any> {
		return this.http
			.post<Kernbenutzer>(this.REST_API_USERDATA + 'bearbeiten', kernbenutzer, {
				headers: new HttpHeaders({ 'Content-Type': 'application/json' })
			}).pipe(
				share(), // Um mehrfachaufrufe zu verhindern
				map(response => {
					this.storeBenutzerdaten(response);
					return response;
				})
			);
	}

	public isAuthenticated(): boolean {
		// Note: tokenNotExpired will by default assume the token name is id_token unless a token name is passed to it,
		// ex: tokenNotExpired('token_name').
		// This will be changed in a future release to automatically use the token name that is set in AuthConfig.
		let isTokenExpired: boolean = this.tokenExists() ? this.token.isTokenExpired() : true;
		if (this.tokenExists() && this.token.isTokenExpired()) {
			this.removeTokenFromStore();
			this.removeBenutzerdatenFromStore();
			this.sessService.removeSessionStore();
			this.router.navigate(['/signin']);
		}
		return !isTokenExpired;
	}

	public storeJwToken(tokenString: string, eingeloggtseit?: number) {
		this._logger.debug(PATH_FILE+' - storeJwToken: eingeloggtseit: '+eingeloggtseit+' und mit tokenString: '+tokenString);
		this.token = new Token(tokenString, this.jwtHelper);
		localStorage.setItem(oase_config.auth_token.name, tokenString);
		this.getAndStoreBenutzerdaten(eingeloggtseit);
	}

	public getAuthUrl(url: string) {
		if (!this.tokenExists()) {
			return null;
		}
		return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'access_token=' + this.token.encoded;
	}

	public storeNewToken(response: HttpResponse<any>) {
		console.log(PATH_FILE+' - storeNewToken');
		if (response?.headers?.get(oase_config.auth_token.name) != null) {
			this.storeJwToken(response.headers.get(oase_config.auth_token.name));
		}
	}

	public getToken(): string {
		return this.token.encoded;
	}

	public getBenutzerdaten(): Kernbenutzer {
		return this.kernbenutzer;
	}

	public getUsername(): string {
		if (!this.tokenExists()) {
			return null;
		}
		return this.token.decoded.preferred_username;
	}

	public getUserfullname(): string {
		if (!this.benutzerdatenExists()) {
			return null;
		}
		return this.kernbenutzer.name;
	}

	public getEingeloggtseit(): number {
		if (!this.benutzerdatenExists()) {
			return null;
		}
		return this.kernbenutzer.eingeloggtseit;
	}

	public getUseremail(): string {
		if (!this.benutzerdatenExists()) {
			return null;
		}
		return this.kernbenutzer.email;
	}

	public getUserimage(): string {
		if (!this.tokenExists()) {
			return null;
		}
		return 'assets/benutzer_standard.jpg';
	}

	public getLeftTimeSec(current: Date): number {
		if (current != null && this.token.expireDate != null) {
			return (this.token.expireDate.getTime() - current.getTime()) / 1000;
		}
		return 0;
	}

	public getMaxTimeSec(): number {
		if (this.token.createDate != null && this.token.expireDate != null) {
			return (this.token.expireDate.getTime() - this.token.createDate.getTime()) / 1000;
		}
		return 0;
	}

	public hasAuthority(authoritiy: string): boolean {
		return this.isAuthenticated() && this.token.authorities.has(authoritiy);
	}

	public isSuperAdmin(): boolean {
		return this.hasAuthority(oase_config.rights.superadmin);
	}

	public benutzerdatenExists() {
		return this.kernbenutzer != null;
	}

	public logTokenInfos(prefix: string) {
		this._logger.log(prefix + ' decodeToken: ', this.token);
		this._logger.log(prefix + ' tokenExpireDate: ', this.tokenExists() ? this.token.expireDate : null);
		this._logger.log(prefix + ' isTokenExpired: ', this.tokenExists() ? this.token.isTokenExpired() : null);
		this._logger.log(prefix + ' isAuthenticated: ', this.isAuthenticated());
	}

	public hatFuhrlohnabrechnung(): boolean {
		if (!this.benutzerdatenExists())
			return null;
		return this.kernbenutzer.hatFuhrlohnabrechnung;
	}


	public hatRecht(recht: string): boolean {
		let hasAuthority: boolean = false;
		let i: number = 0;

		this._logger.log(PATH_FILE + ' - KERN GRUPPEN: ', this.kernbenutzer.kernGruppen);
		while (i < this.kernbenutzer.kernGruppen.length) {
			let x = 0;
			this._logger.log(PATH_FILE + ' - KERN RECHTE: ', this.kernbenutzer.kernGruppen[i].kernRechte);
			while (x < this.kernbenutzer.kernGruppen[i].kernRechte.length && recht != this.kernbenutzer.kernGruppen[i].kernRechte[x].sprachschluessel) {
				x++;
			}
			if (x < this.kernbenutzer.kernGruppen[i].kernRechte.length) {
				i = this.kernbenutzer.kernGruppen.length;
				hasAuthority = true;
			}
			i++;
		}
		return hasAuthority;
	}
}